// Sales analytics helpers. Pure functions — take shaped appointments (from
// /api/staff/appointments) and compute KPIs + trends + report rows.
//
// Revenue definition (per business rule):
//   Revenue        = SUM(price) where status='approved' AND payment.status='paid'
//   Deposits       = SUM(payment.amount)   where payment.status='paid'
//   Outstanding    = SUM(max(0, price - paidAmount)) for status in {approved, pending}
//
// UK financial year: 6 April YY -> 5 April YY+1.

/* -------------- financial year helpers -------------- */
export function currentFinancialYearStart(now = new Date()) {
    const y = now.getFullYear();
    // FY starts on 6 April
    return now >= new Date(y, 3, 6) ? y : y - 1;
}

export function financialYearBounds(fyStartYear) {
    // Inclusive dates. FY26/27 => 2026-04-06 .. 2027-04-05
    return { start: `${fyStartYear}-04-06`, end: `${fyStartYear + 1}-04-05` };
}

export function financialYearLabel(fyStartYear) {
    const short = (y) => String(y).slice(-2);
    return `${fyStartYear}/${short(fyStartYear + 1)}`;
}

export function availableFinancialYears(count = 5) {
    const cur = currentFinancialYearStart();
    // next FY + current + past (count-1) prior years
    const list = [];
    list.push(cur + 1);
    for (let i = 0; i < count; i++) list.push(cur - i);
    return list;
}

/* -------------- date helpers -------------- */
export function toISO(d) { return d.toISOString().slice(0, 10); }
export function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }

export function rangeFor(preset, now = new Date()) {
    const today = toISO(now);
    if (preset === "today") return { start: today, end: today };
    if (preset === "week") {
        const day = (now.getDay() + 6) % 7; // ISO Mon=0
        const start = addDays(now, -day);
        return { start: toISO(start), end: toISO(addDays(start, 6)) };
    }
    if (preset === "month") {
        return {
            start: toISO(new Date(now.getFullYear(), now.getMonth(), 1)),
            end:   toISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
    }
    if (preset === "year") {
        return financialYearBounds(currentFinancialYearStart(now));
    }
    return { start: today, end: today };
}

/* -------------- revenue predicates -------------- */
const statusLc = (a) => String(a.status || "").toLowerCase();
const paymentStatusLc = (a) => String(a.payment?.status || "").toLowerCase();
export const isRevenueEligible = (a) => statusLc(a) === "approved" && paymentStatusLc(a) === "paid";
export const paidAmount = (a) => (paymentStatusLc(a) === "paid" ? Number(a.payment?.amount || 0) : 0);
export const revenueValue = (a) => (isRevenueEligible(a) ? Number(a.price || 0) : 0);
export const outstandingValue = (a) => {
    const st = statusLc(a);
    if (st !== "approved" && st !== "pending") return 0;
    return Math.max(0, Number(a.price || 0) - paidAmount(a));
};
export const isActive = (a) => !["canceled", "cancelled", "rejected"].includes(statusLc(a));

/* -------------- summarise -------------- */
function enumerateDays(startISO, endISO) {
    const out = [];
    const s = new Date(`${startISO}T00:00:00`);
    const e = new Date(`${endISO}T00:00:00`);
    for (let d = new Date(s); d <= e; d = addDays(d, 1)) out.push(toISO(d));
    return out;
}

/**
 * @param apptsAll - full appointment dataset (for customer new/returning correctness)
 * @param range    - { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
 */
export function summarise(apptsAll, range) {
    const inRange = (iso) => iso && iso.slice(0, 10) >= range.start && iso.slice(0, 10) <= range.end;
    const rangeAppts = apptsAll.filter((a) => inRange(a.bookingStart));

    const totalRevenue = rangeAppts.reduce((s, a) => s + revenueValue(a), 0);
    const depositsReceived = rangeAppts.reduce((s, a) => s + paidAmount(a), 0);
    const outstandingPayments = rangeAppts.reduce((s, a) => s + outstandingValue(a), 0);
    const appointmentCount = rangeAppts.filter(isActive).length;

    // Customer first-appointment lookup uses the WHOLE dataset for accuracy.
    const firstSeen = new Map();
    apptsAll.forEach((a) => {
        if (!a.customerId) return;
        const dt = (a.bookingStart || "").slice(0, 10);
        if (!dt) return;
        const cur = firstSeen.get(a.customerId);
        if (!cur || dt < cur) firstSeen.set(a.customerId, dt);
    });
    const custInRange = new Set(rangeAppts.filter(isActive).map((a) => a.customerId).filter(Boolean));
    const newCustomerCount = [...custInRange].filter((cid) => {
        const first = firstSeen.get(cid);
        return first && first >= range.start && first <= range.end;
    }).length;
    const returningCount = custInRange.size - newCustomerCount;
    const returningPct = custInRange.size > 0 ? (returningCount / custInRange.size) * 100 : 0;
    const averageSpend = custInRange.size > 0 ? totalRevenue / custInRange.size : 0;

    // treatment aggregations
    const byService = new Map();
    rangeAppts.forEach((a) => {
        const name = a.serviceName || `Service #${a.serviceId ?? "?"}`;
        const cur = byService.get(name) || { name, count: 0, revenue: 0 };
        if (isActive(a)) cur.count += 1;
        cur.revenue += revenueValue(a);
        byService.set(name, cur);
    });
    const services = [...byService.values()].sort((a, b) => b.count - a.count);
    const mostPopularTreatment = services[0] || null;
    const highestRevenueTreatment = [...services].sort((a, b) => b.revenue - a.revenue)[0] || null;

    // trend by day
    const days = enumerateDays(range.start, range.end);
    const trendMap = Object.fromEntries(days.map((d) => [d, { date: d, revenue: 0, appointments: 0 }]));
    rangeAppts.forEach((a) => {
        const d = (a.bookingStart || "").slice(0, 10);
        if (trendMap[d]) {
            trendMap[d].revenue += revenueValue(a);
            if (isActive(a)) trendMap[d].appointments += 1;
        }
    });
    const trend = days.map((d) => trendMap[d]);

    return {
        range,
        totalRevenue,
        depositsReceived,
        outstandingPayments,
        appointmentCount,
        uniqueCustomers: custInRange.size,
        newCustomerCount,
        returningCount,
        returningPct,
        averageSpend,
        mostPopularTreatment,
        highestRevenueTreatment,
        services,
        trend,
        rangeAppts,
    };
}

/* -------------- report row builders -------------- */
export const REPORTS = {
    revenue_summary: {
        title: "Revenue Summary",
        headers: ["Metric", "Value (GBP)"],
        build: (s) => [
            ["Total Revenue (approved + paid)", s.totalRevenue.toFixed(2)],
            ["Deposits Received", s.depositsReceived.toFixed(2)],
            ["Outstanding Payments", s.outstandingPayments.toFixed(2)],
            ["Appointment Count", s.appointmentCount],
            ["Unique Customers", s.uniqueCustomers],
            ["New Customers", s.newCustomerCount],
            ["Returning Customers", s.returningCount],
            ["Returning %", `${s.returningPct.toFixed(1)}%`],
            ["Average Spend / Client", s.averageSpend.toFixed(2)],
            ["Most Popular Treatment", s.mostPopularTreatment?.name || "—"],
            ["Highest Revenue Treatment", s.highestRevenueTreatment?.name || "—"],
        ],
    },
    deposits: {
        title: "Deposits",
        headers: ["Date", "Appointment", "Customer", "Service", "Deposit (GBP)", "Gateway"],
        build: (s) => s.rangeAppts
            .filter((a) => paidAmount(a) > 0)
            .map((a) => [
                (a.bookingStart || "").slice(0, 16),
                a.id,
                `${a.customer?.firstName || ""} ${a.customer?.lastName || ""}`.trim(),
                a.serviceName || `#${a.serviceId}`,
                paidAmount(a).toFixed(2),
                a.payment?.gateway || "",
            ]),
    },
    outstanding: {
        title: "Outstanding Balances",
        headers: ["Date", "Appointment", "Customer", "Service", "Price (GBP)", "Paid (GBP)", "Outstanding (GBP)", "Status"],
        build: (s) => s.rangeAppts
            .filter((a) => outstandingValue(a) > 0)
            .map((a) => [
                (a.bookingStart || "").slice(0, 16),
                a.id,
                `${a.customer?.firstName || ""} ${a.customer?.lastName || ""}`.trim(),
                a.serviceName || `#${a.serviceId}`,
                Number(a.price || 0).toFixed(2),
                paidAmount(a).toFixed(2),
                outstandingValue(a).toFixed(2),
                a.status,
            ]),
    },
    payment_methods: {
        title: "Payment Method Breakdown",
        headers: ["Gateway", "Transactions", "Total (GBP)"],
        build: (s) => {
            const m = new Map();
            s.rangeAppts.forEach((a) => {
                if (paidAmount(a) <= 0) return;
                const gw = a.payment?.gateway || "unknown";
                const cur = m.get(gw) || { count: 0, total: 0 };
                cur.count += 1; cur.total += paidAmount(a);
                m.set(gw, cur);
            });
            return [...m.entries()].map(([gw, v]) => [gw, v.count, v.total.toFixed(2)]);
        },
    },
    revenue_by_treatment: {
        title: "Revenue by Treatment",
        headers: ["Treatment", "Bookings", "Revenue (GBP)"],
        build: (s) => s.services.map((sv) => [sv.name, sv.count, sv.revenue.toFixed(2)]),
    },
    revenue_by_employee: {
        title: "Revenue by Employee",
        headers: ["Employee ID", "Bookings", "Revenue (GBP)"],
        build: (s) => {
            const m = new Map();
            s.rangeAppts.forEach((a) => {
                const key = a.providerId ?? "unassigned";
                const cur = m.get(key) || { count: 0, revenue: 0 };
                if (isActive(a)) cur.count += 1;
                cur.revenue += revenueValue(a);
                m.set(key, cur);
            });
            return [...m.entries()].map(([id, v]) => [id, v.count, v.revenue.toFixed(2)]);
        },
    },
    monthly_breakdown: {
        title: "Monthly Breakdown",
        headers: ["Month", "Bookings", "Revenue (GBP)", "Deposits (GBP)"],
        build: (s) => {
            const m = new Map();
            s.rangeAppts.forEach((a) => {
                const mm = (a.bookingStart || "").slice(0, 7);
                if (!mm) return;
                const cur = m.get(mm) || { count: 0, revenue: 0, deposits: 0 };
                if (isActive(a)) cur.count += 1;
                cur.revenue += revenueValue(a);
                cur.deposits += paidAmount(a);
                m.set(mm, cur);
            });
            return [...m.entries()]
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([mm, v]) => [mm, v.count, v.revenue.toFixed(2), v.deposits.toFixed(2)]);
        },
    },
    annual_tax: {
        title: "Annual Tax Summary",
        headers: ["Metric", "Value (GBP)"],
        build: (s) => [
            ["Tax Year", `${s.range.start} to ${s.range.end}`],
            ["Gross Revenue (approved+paid)", s.totalRevenue.toFixed(2)],
            ["Deposits Received", s.depositsReceived.toFixed(2)],
            ["Outstanding at Period End", s.outstandingPayments.toFixed(2)],
            ["Total Appointments", s.appointmentCount],
            ["Unique Customers", s.uniqueCustomers],
        ],
    },
};
